import {
  ECardinality,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaColorPicker from "../../../ui-kit/kuika-color-picker/kuika-color-picker";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

/** The icons a confirmation dialog can lead with; the same list the action's own panel offers. */
const CONFIRMATION_ICON_OPTIONS: KuikaSelectboxOption[] = [
  { label: "Confirmation", value: "confirmation" },
  { label: "Warning", value: "warning" },
  { label: "Trash", value: "trash" },
  { label: "Info", value: "info" },
  { label: "Question", value: "question" },
  { label: "Notification", value: "notification" }
];

/** Swatch fallbacks — must mirror the code generator's defaults so the picker
 * never shows a colour the generated app would not actually use. */
const DEFAULT_CONFIRM_BUTTON_COLOR: string = "#246eff";
const DEFAULT_CANCEL_BUTTON_COLOR: string = "#246eff";

export interface IConfirmationSymbolGroupState {
  searchText: string;
}

export interface IConfirmationSymbolGroupProps {
  action: IAction;
  onActionSaved: (action: IAction, changeReason?: ESdChangeReason) => void;
}

class ConfirmationSymbolGroup extends PureSdComponent<
  IConfirmationSymbolGroupProps & IWithSDProps,
  IConfirmationSymbolGroupState
> {
  constructor(props: IConfirmationSymbolGroupProps & IWithSDProps) {
    super(props);
    this.state = {
      searchText: ""
    };
  }

  componentDidMount() {
    let fontFamily = this.props.action.parameters.find((p) => p.name === "confirmationFontFamily");
    let header = this.props.action.parameters.find((p) => p.name === "confirmationHeader");
    let icon = this.props.action.parameters.find((p) => p.name === "confirmationIcon");
    let iconBackgroundColor = this.props.action.parameters.find((p) => p.name === "confirmationIconBackgroundColor");
    let confirmButtonColor = this.props.action.parameters.find((p) => p.name === "confirmationConfirmButtonColor");
    let cancelButtonColor = this.props.action.parameters.find((p) => p.name === "confirmationCancelButtonColor");

    if (!fontFamily) {
      fontFamily = {
        name: "confirmationFontFamily",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "Poppins",
          isFixed: true
        }
      };

      this.props.action.parameters.push(fontFamily);
      this.props.onActionSaved({ ...this.props.action }, ESdChangeReason.DiActionUpdated);
    } else if (
      fontFamily?.symbol.category === ESymbolCategory.fixed &&
      (!_.isString(fontFamily?.symbol?.fixedValue) || _.isEmpty(fontFamily?.symbol?.fixedValue?.trim()))
    ) {
      const fontFamilyIndex = this.props.action.parameters.findIndex((p) => p.name === "confirmationFontFamily");
      fontFamily = {
        ...fontFamily,
        symbol: {
          ...fontFamily.symbol,
          fixedValue: "Poppins",
          category: ESymbolCategory.fixed,
          isFixed: true
        }
      };

      this.props.action.parameters[fontFamilyIndex] = fontFamily;
      this.props.onActionSaved({ ...this.props.action }, ESdChangeReason.DiActionUpdated);
    }

    if (!header) {
      header = {
        name: "confirmationHeader",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "Confirmation",
          isFixed: true
        }
      };

      this.props.action.parameters.push(header);
      this.props.onActionSaved({ ...this.props.action }, ESdChangeReason.DiActionUpdated);
    }

    if (!icon) {
      icon = {
        name: "confirmationIcon",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "confirmation",
          isFixed: true
        }
      };

      this.props.action.parameters.push(icon);
      this.props.onActionSaved({ ...this.props.action }, ESdChangeReason.DiActionUpdated);
    }

    if (!iconBackgroundColor) {
      iconBackgroundColor = {
        name: "confirmationIconBackgroundColor",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "#246dff",
          isFixed: true
        }
      };

      this.props.action.parameters.push(iconBackgroundColor);
      this.props.onActionSaved({ ...this.props.action }, ESdChangeReason.DiActionUpdated);
    }

    // Order matters: the action template fills {aN} positionally from this list,
    // so new parameters are always appended after the existing ones.
    if (!confirmButtonColor) {
      confirmButtonColor = {
        name: "confirmationConfirmButtonColor",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: DEFAULT_CONFIRM_BUTTON_COLOR,
          isFixed: true
        }
      };

      this.props.action.parameters.push(confirmButtonColor);
      this.props.onActionSaved({ ...this.props.action }, ESdChangeReason.DiActionUpdated);
    }

    if (!cancelButtonColor) {
      cancelButtonColor = {
        name: "confirmationCancelButtonColor",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: DEFAULT_CANCEL_BUTTON_COLOR,
          isFixed: true
        }
      };

      this.props.action.parameters.push(cancelButtonColor);
      this.props.onActionSaved({ ...this.props.action }, ESdChangeReason.DiActionUpdated);
    }
  }

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const parameterIndex = this.props.action.parameters.findIndex((p) => p.name === parameter.name);
    this.props.action.parameters[parameterIndex] = { ...parameter, symbol };
    this.props.onActionSaved({ ...this.props.action }, changeReason);
  };

  renderConfirmationMessage() {
    const message = this.props.action.parameters.find((p) => p.name === "confirmationMessage");

    if (!message) {
      const newMessage: IActionParameter = {
        name: "confirmationMessage",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "",
          isFixed: true
        }
      };
      this.props.action.parameters.push(newMessage);
      return this.renderConfirmationMessage();
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          <ActionParameterTitle
            parameter={{ name: "Confirmation Message", Typename: "String", cardinality: ECardinality.One }}
          />
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={message.symbol}
          valueType={"String"}
          targetCardinality={message.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(message, symbol, changeReason)
          }
          data_testid="confirmationMessage"
          bindingParameter={message}
          bindingLabel="Confirmation Message"
        />
      </div>
    );
  }

  renderConfirmationHeader() {
    if (this.props.sd.project.projectType !== EProjectType.Desktop) {
      return null;
    }
    const header = this.props.action.parameters.find((p) => p.name === "confirmationHeader");

    if (!header) {
      const newHeader: IActionParameter = {
        name: "confirmationHeader",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "",
          isFixed: true
        }
      };
      this.props.action.parameters.push(newHeader);
      return this.renderConfirmationHeader();
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          <ActionParameterTitle
            parameter={{ name: "Confirmation Header", Typename: "String", cardinality: ECardinality.One }}
          />
        </div>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={header.symbol}
          valueType={"String"}
          targetCardinality={header.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(header, symbol, changeReason)
          }
          data_testid="confirmationHeader"
          bindingParameter={header}
          bindingLabel="Confirmation Header"
        />
      </div>
    );
  }

  renderConfirmationIcon() {
    if (this.props.sd.project.projectType !== EProjectType.Desktop) {
      return null;
    }
    const icon = this.props.action.parameters.find((p) => p.name === "confirmationIcon");

    if (!icon) {
      const newIcon: IActionParameter = {
        name: "confirmationIcon",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "",
          isFixed: true
        }
      };
      this.props.action.parameters.push(newIcon);
      return this.renderConfirmationIcon();
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          <ActionParameterTitle
            parameter={{ name: "Confirmation Icon", Typename: "String", cardinality: ECardinality.One }}
          />
        </div>
        <KuikaSelectbox
          value={icon.symbol.fixedValue || ""}
          // Clearing reports `null`; the parameter stores "no icon" as an empty string.
          onChange={(value: string | number | (string | number)[] | null): void => {
            const symbol: ISymbol = {
              ...icon.symbol,
              fixedValue: typeof value === "string" ? value : "",
              category: ESymbolCategory.fixed,
              isFixed: true
            };
            this.handleSelectSymbolPickerValue(icon, symbol);
          }}
          allowClear
          options={CONFIRMATION_ICON_OPTIONS}
        />
      </div>
    );
  }

  renderConfirmationIconBackgroundColor() {
    if (this.props.sd.project.projectType !== EProjectType.Desktop) {
      return null;
    }
    const iconBackgroundColor = this.props.action.parameters.find((p) => p.name === "confirmationIconBackgroundColor");

    if (!iconBackgroundColor) {
      const newIconBackgroundColor: IActionParameter = {
        name: "confirmationIconBackgroundColor",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "#246dff",
          isFixed: true
        }
      };
      this.props.action.parameters.push(newIconBackgroundColor);
      return this.renderConfirmationIconBackgroundColor();
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          <ActionParameterTitle
            parameter={{ name: "Icon Background Color", Typename: "String", cardinality: ECardinality.One }}
          />
        </div>
        <KuikaColorPicker
          color={iconBackgroundColor.symbol.fixedValue || "#246dff"}
          onChange={(color: string): void => {
            this.handleSelectSymbolPickerValue(iconBackgroundColor, {
              ...iconBackgroundColor.symbol,
              fixedValue: color
            });
          }}
          dataTestId="confirmationIconBackgroundColorSymbolPicker"
        />
      </div>
    );
  }

  /** Confirm / Cancel button colour pickers — same shape, so one renderer. */
  renderConfirmationButtonColor(parameterName: string, label: string, defaultColor: string) {
    if (this.props.sd.project.projectType !== EProjectType.Desktop) {
      return null;
    }
    const buttonColor = this.props.action.parameters.find((p) => p.name === parameterName);

    if (!buttonColor) {
      const newButtonColor: IActionParameter = {
        name: parameterName,
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: defaultColor,
          isFixed: true
        }
      };
      this.props.action.parameters.push(newButtonColor);
      return this.renderConfirmationButtonColor(parameterName, label, defaultColor);
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          <ActionParameterTitle parameter={{ name: label, Typename: "String", cardinality: ECardinality.One }} />
        </div>
        <KuikaColorPicker
          color={buttonColor.symbol.fixedValue || defaultColor}
          onChange={(color: string): void => {
            this.handleSelectSymbolPickerValue(buttonColor, {
              ...buttonColor.symbol,
              fixedValue: color
            });
          }}
          dataTestId={`${parameterName}SymbolPicker`}
        />
      </div>
    );
  }

  renderConfirmationFontFamily() {
    if (this.props.sd.project.projectType !== EProjectType.Desktop) {
      return null;
    }

    const fontFamily = this.props.action.parameters.find((p) => p.name === "confirmationFontFamily");

    if (!fontFamily) {
      const newFontFamily: IActionParameter = {
        name: "confirmationFontFamily",
        cardinality: ECardinality.One,
        Typename: "String",
        symbol: {
          label: "Fixed Value",
          description: "",
          category: ESymbolCategory.fixed,
          fixedValue: "Poppins",
          isFixed: true
        }
      };
      this.props.action.parameters.push(newFontFamily);
      return this.renderConfirmationFontFamily();
    }

    return (
      <div>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          <ActionParameterTitle
            parameter={{ name: "Font Family", Typename: "String", cardinality: ECardinality.One }}
          />
        </div>
        <KuikaSelectbox
          value={fontFamily.symbol.fixedValue || "Poppins"}
          onChange={(value: string | number | (string | number)[] | null): void => {
            if (typeof value !== "string") return;
            const symbol: ISymbol = {
              ...fontFamily.symbol,
              fixedValue: value,
              category: ESymbolCategory.fixed,
              isFixed: true
            };
            this.handleSelectSymbolPickerValue(fontFamily, symbol);
          }}
          options={(this.props.sd?.project?.componentSetObj?.fontNames ?? []).map(
            (font: string): KuikaSelectboxOption => ({ label: font, value: font })
          )}
        />
      </div>
    );
  }

  render() {
    return (
      <div className="kuika_property-grid-column">
        {this.renderConfirmationMessage()}
        {this.renderConfirmationFontFamily()}
        {this.renderConfirmationHeader()}
        {this.renderConfirmationIcon()}
        {this.renderConfirmationIconBackgroundColor()}
        {this.renderConfirmationButtonColor(
          "confirmationConfirmButtonColor",
          "Confirm Button Color",
          DEFAULT_CONFIRM_BUTTON_COLOR
        )}
        {this.renderConfirmationButtonColor(
          "confirmationCancelButtonColor",
          "Cancel Button Color",
          DEFAULT_CANCEL_BUTTON_COLOR
        )}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ConfirmationMessageChanged ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const confirmationSymbolGroup = withSD<IConfirmationSymbolGroupProps>(ConfirmationSymbolGroup);
export { confirmationSymbolGroup as ConfirmationSymbolGroup };
